import { inject, Injectable } from '@angular/core';
import { map, Observable, take } from 'rxjs';
import { RolePermissionEnum } from '../core/enums/role-permission.enum';
import { UserInterface } from '../core/models/user.model';
import { PermissionsUserInterface, PermissionsUserService } from './user.model';

export interface ActionTypePermissionInterface<T> {
  action: T;
  permission: RolePermissionEnum;
  disabled?: boolean;
}

export interface PermissionsDecisionMakerServiceInterface {
  shouldAllow(actions: string[]): Observable<boolean>;

  setPermissions(permissions: Record<string, string[]>);

  setRoles(roles: string[]);

  getAllowedActions<T>(mappings: ActionTypePermissionInterface<T>[]): Observable<T[]>;
}

export abstract class PermissionsDecisionMakerService implements PermissionsDecisionMakerServiceInterface {
  abstract shouldAllow(actions: string[]): Observable<boolean>;

  abstract setPermissions(permissions: Record<string, string[]>);

  abstract setRoles(roles: string[]);

  abstract getAllowedActions<T>(mappings: ActionTypePermissionInterface<T>[]): Observable<T[]>;
}

@Injectable()
export class DecisionMakerService extends PermissionsDecisionMakerService {
  private permissionsUserService = inject(PermissionsUserService);

  private roles: string[] = [];
  private permissions: Record<string, string[]> = {};

  setPermissions(permissions: Record<string, string[]> = {}) {
    this.permissions = permissions;
    return this;
  }

  setRoles(roles: string[] = []) {
    this.roles = roles;
    return this;
  }

  shouldAllow(actions: string[]): Observable<boolean> {
    return new Observable((observer) => {
      this.permissionsUserService.getUser().subscribe((user: PermissionsUserInterface) => {
        const shouldAllow = this.deepCheck(actions, user);
        observer.next(shouldAllow);
        observer.complete();
      });
    });
  }

  getAllowedActions<T>(mappings: ActionTypePermissionInterface<T>[]): Observable<T[]> {
    return this.permissionsUserService.getUser().pipe(
      take(1),
      map((user) => mappings.filter((m) => !m.disabled && this.deepCheck([m.permission], user)).map((m) => m.action)),
    );
  }

  private deepCheck(actions: string[], user: UserInterface): boolean {
    const roleName = user?.role?.name;
    if (!roleName || !this.roles.includes(roleName)) {
      return false;
    }

    const rolePermissions = this.permissions[roleName] || [];
    return actions.some((action) => rolePermissions.includes(action));
  }
}
